import { useMemo } from 'react';
import { useFtpStore } from '../store/ftpStore';
import { useTeamVaults } from './useTeamVaults';
import { selectTeamEntry } from '../store/teamVaultStore';
import { buildDestinations } from '../utils/vaultDestinations';
import { mergeConnectionRows } from '../utils/ftpConnectionRows';

/**
 * Every SFTP/FTP connection this user can reach — personal vault plus every
 * team vault they hold a grant in — as display rows, and the destinations a
 * NEW one may be saved to.
 *
 * Extracted because three surfaces need the same answer and had drifted apart:
 * the FTP sidebar (which had it), the FTP view's own "which connection is
 * active" lookup (which did NOT, so a connected team row rendered the
 * "no connection selected" empty state forever), and the Terminal's SSH
 * connection picker (which did not, so team SSH was invisible). One derivation,
 * one set of rules.
 *
 * "FOR DISPLAY ONLY" is still literal and still load-bearing: the two lists are
 * merged HERE, in a hook components call, and never in a store.
 * `ftpStore.connections` stays exactly what it always was — `vault_list`
 * filtered to `sftp` — because merging team items into anything derived from
 * `vault_list` is an exploitable vulnerability, not a tidy-up
 * (`services/teamVaultService.js`'s header, `tests/teamVaultIsolation.test.js`).
 *
 * Every row is tagged with `origin`, and a team row's id is
 * `team:<teamId>:<itemId>`, which no personal id can equal. Consumers route on
 * the tag; the id shape is the belt to that braces.
 *
 * SELECTOR DISCIPLINE: `useTeamVaults()` returns RAW store slices (both stable
 * references) and everything derived from them is wrapped in `useMemo`. A
 * fresh array handed to a zustand hook makes `useSyncExternalStore` see a new
 * snapshot on every render and React aborts the panel with "Maximum update
 * depth exceeded" — this codebase has shipped that exact bug once already.
 */
export function useConnectionRows() {
  const connections = useFtpStore((s) => s.connections);
  // A raw store slice, like `connections` — see SELECTOR DISCIPLINE above.
  const tempConnections = useFtpStore((s) => s.tempConnections);
  const { teams, byTeam } = useTeamVaults();

  const teamGroups = useMemo(
    () =>
      teams
        .filter((team) => team?.id != null)
        .map((team) => ({
          teamId: team.id,
          teamName: team.name || '',
          items: selectTeamEntry(byTeam, team.id).items,
        })),
    [teams, byTeam]
  );

  const rows = useMemo(
    () => mergeConnectionRows(connections, teamGroups, tempConnections),
    [connections, teamGroups, tempConnections]
  );

  const destinations = useMemo(() => buildDestinations({ teams, byTeam }), [teams, byTeam]);

  return { rows, destinations, teams, byTeam };
}

export default useConnectionRows;
